// Copyright (c) Ping. All rights reserved.
//
// Bridges a parsed AppEvent into the existing `ChatMessageVariant`
// sidecar shape consumed by `MessageRow`. The shape predates the
// envelope registry — keeping it untouched means apps/web and
// apps/desktop's host wrappers can adopt envelope-driven dispatch
// without a coordinated cut-over.
//
// What this returns:
//   - For renderable kinds with an existing tile component, a
//     `ChatMessageVariant`. The renderer falls back to its `MessageRow`
//     default (plain text bubble) if we return `undefined`.
//   - For sidecar / unrenderable kinds, `undefined` — the row stays
//     plain text and the bubble displays the raw body string.
//
// Host responsibility:
//   - The variant carries callbacks (`onRetry`, `onTogglePlay`, etc).
//     The host injects them through `DeriveVariantCtx.callbacks` —
//     this module wires the right callback into the right kind.
//   - The host injects per-row state (upload progress, playback
//     progress) the same way.

import type {
  DocAttachmentState,
} from '../chat/attachments/DocAttachmentState';
import type {
  ImageAttachmentState,
} from '../chat/attachments/ImageAttachmentState';
import type {
  EventMessageType,
} from '../chat/EventMessageType';
import type {
  VoiceMessageState,
} from '../chat/VoiceMessageState';
import { Linking } from 'react-native';
import type { Message } from '../sync/persistence';
import type { ChatMessageVariant } from '../chat/ChatRoom.types';
import type { PollVoterAvatar } from '../polls/PollView';
import { parseEnvelope, type ParseResult } from './envelope';
import type {
  CallEndBody,
  CallInviteBody,
  FileBody,
  GameCreateBody,
  GameKind,
  GameMoveBody,
  GameOutcomeBody,
  GroupAvatarBody,
  GroupNameBody,
  GroupTopicBody,
  ImageBody,
  InviteBody,
  KickReasonBody,
  PinBody,
  UnpinBody,
  VideoBody,
  VoiceBody,
} from './types';

export interface VoiceVariantCallbacks {
  readonly onTogglePlay: () => void;
  readonly onRetry?: (() => void) | undefined;
  readonly progress?: number | undefined;
}

export interface ImageVariantCallbacks {
  readonly onTapOpen: () => void;
  readonly onRetry?: (() => void) | undefined;
  readonly progress?: number | undefined;
  /**
   * Host-resolved, directly-viewable image URL (e.g. an object URL minted
   * after fetching + decrypting an encrypted S3 blob). When supplied it
   * overrides the envelope's `blob_ref.url`, which is NOT viewable for
   * encrypted media. Surfaces without a resolver (display-only) omit this
   * and fall back to the envelope url.
   */
  readonly imageUri?: string | undefined;
}

/**
 * Host-resolved poll vote projection. When supplied, the poll variant
 * renders a LIVE tally + a working `onVote` (mirroring the main chat).
 * When absent, the poll case falls back to a display-only result card.
 */
export interface PollVariantContext {
  readonly counts: Record<number, number>;
  readonly myVote?: number | undefined;
  readonly myVotes?: readonly number[] | undefined;
  readonly total: number;
  readonly votersByOption?:
    | Record<number, ReadonlyArray<PollVoterAvatar>>
    | undefined;
  readonly onVote: (optionIndex: number) => void;
  readonly onViewVotes?: (() => void) | undefined;
}

export interface DocVariantCallbacks {
  readonly onTapPreview: () => void;
  readonly onRetry?: (() => void) | undefined;
  readonly progress?: number | undefined;
  /** Host-rendered first-page thumbnail → triggers the wide preview tile. */
  readonly previewUri?: string | undefined;
}

/**
 * Live meeting projection + RSVP/creator callbacks. Supplied by hosts that own
 * the materialized `MeetingState` (folded from the `com.ping.meeting*` stream).
 * When absent the meeting case renders a display-only card from the create body
 * (title/time/location/link/description) with no RSVP tally.
 */
export interface MeetingVariantContext {
  readonly status: import('../meetings/meetingState').MeetingStatus;
  readonly counts: import('../meetings/meetingState').RsvpCounts;
  readonly myRsvp?: import('./types').MeetingRsvpResponse | undefined;
  readonly isCreator: boolean;
  /** Live fields resolved from updates (override the create body). */
  readonly title?: string | undefined;
  readonly startsAt?: number | undefined;
  readonly endsAt?: number | undefined;
  readonly location?: string | undefined;
  readonly description?: string | undefined;
  readonly meetingLink?: string | undefined;
  readonly reminderMinutes?: number | undefined;
  /** Live `call`-type flag resolved from state/body — enables "Join call". */
  readonly autoCall?: { readonly media: 'audio' | 'video' } | undefined;
  readonly onRsvp?: ((response: import('./types').MeetingRsvpResponse) => void) | undefined;
  readonly onOpenLink?: ((url: string) => void) | undefined;
  readonly onCopyLink?: ((url: string) => void) | undefined;
  readonly onEdit?: (() => void) | undefined;
  readonly onCancelMeeting?: (() => void) | undefined;
  readonly onShowResponses?: (() => void) | undefined;
  readonly onJoinCall?: ((media: 'audio' | 'video') => void) | undefined;
}

export interface VideoVariantCallbacks {
  readonly onTapOpen: () => void;
  readonly onRetry?: (() => void) | undefined;
  readonly onDownload?: (() => void) | undefined;
  readonly progress?: number | undefined;
  /**
   * Host-resolved, directly-playable video object URL (decrypted from the
   * encrypted S3 blob). Until present the card shows the poster only.
   */
  readonly videoUri?: string | undefined;
  /** Host-resolved poster object URL (overrides the envelope poster_ref). */
  readonly posterUri?: string | undefined;
}

/**
 * Host-resolved link-preview projection. The scraped og: metadata lives
 * on the envelope; the host only injects the `onOpen` (Linking.openURL)
 * and an optional resolved image object URL when the preview image was
 * shipped as an encrypted blob rather than a CDN url.
 */
export interface LinkPreviewVariantContext {
  readonly onOpen: () => void;
  readonly imageUri?: string | undefined;
}

/**
 * Game-host variant. Emitted for `com.ping.game_create` / `_move` /
 * `_outcome` so the host renders an embedded game surface (board /
 * result chip) keyed on `sessionId`.
 *
 * TODO(ChatRoom.types.ts): this member is NOT yet part of the
 * `ChatMessageVariant` union (that file is owned by the integrator).
 * Add the following union member verbatim so `deriveMessageVariant`'s
 * return type type-checks:
 *
 *   | {
 *         readonly kind: "game";
 *         readonly gameKind: GameKind;
 *         readonly sessionId: string;
 *         readonly phase: "create" | "move" | "outcome";
 *         readonly payload: unknown;
 *     }
 *
 * Until that lands, `deriveMessageVariant` casts the game branch
 * through `ChatMessageVariant` so the rest of the module compiles.
 */
export interface GameVariant {
  readonly kind: 'game';
  /** Which game this row hosts (coinflip, chess, …). */
  readonly gameKind: GameKind;
  /** Hex session id — the stable key the host's game state is keyed on. */
  readonly sessionId: string;
  /** Lifecycle phase that produced this row. */
  readonly phase: 'create' | 'move' | 'outcome';
  /**
   * The decoded game body (GameCreateBody | GameMoveBody |
   * GameOutcomeBody). Left as `unknown` on the variant so the host can
   * narrow by `phase`; the ingest agent passes the parsed body through
   * unchanged.
   */
  readonly payload: unknown;
}

/**
 * Host context required to translate a parsed envelope into a usable
 * variant. Anything the variant needs that can't live in the envelope
 * (callbacks, in-flight progress, resolved Blob URLs) flows through
 * here, keyed by the row's `Message.id`.
 */
export interface DeriveVariantCtx {
  readonly message: Message;
  /** Maps message id → playback / upload / open callbacks. */
  readonly voice?: VoiceVariantCallbacks | undefined;
  readonly image?: ImageVariantCallbacks | undefined;
  readonly doc?: DocVariantCallbacks | undefined;
  readonly video?: VideoVariantCallbacks | undefined;
  readonly linkPreview?: LinkPreviewVariantContext | undefined;
  /**
   * Live poll vote projection + `onVote`. Supplied by hosts that own the
   * vote tally (main chat, thread panel). When absent the poll case
   * renders a display-only result card.
   */
  readonly poll?: PollVariantContext | undefined;
  /**
   * Live meeting state + RSVP/creator callbacks. Supplied by hosts that own
   * the materialized `MeetingState`. When absent the meeting card is
   * display-only (create body, no tally).
   */
  readonly meeting?: MeetingVariantContext | undefined;
}

/**
 * Derives a `ChatMessageVariant` (or `undefined` to fall back to the
 * plain text bubble) from a parsed envelope + host context. Pure: no
 * I/O, no side-effects.
 */
export function deriveMessageVariant(
  parsed: ParseResult | undefined,
  ctx: DeriveVariantCtx,
): ChatMessageVariant | undefined {
  if (parsed === undefined) return undefined;
  if (parsed.kind === 'unrenderable') return undefined;
  if (
    parsed.kind === 'com.ping.delivered' ||
    parsed.kind === 'com.ping.read' ||
    parsed.kind === 'ping.profile'
  ) {
    return undefined;
  }

  const { message } = ctx;
  const isFailed = message.deliveryState === 'failed';

  switch (parsed.kind) {
    case 'com.ping.voice': {
      const body = parsed.body as VoiceBody;
      const state = pickVoiceState(message, isFailed);
      const onTogglePlay = ctx.voice?.onTogglePlay ?? noop;
      const onRetry = ctx.voice?.onRetry;
      const progress = ctx.voice?.progress ?? 0;
      return {
        kind: 'voice',
        state,
        durationMs: body.dur_ms,
        waveform: body.waveform,
        progress,
        onTogglePlay,
        ...(onRetry !== undefined ? { onRetry } : {}),
      };
    }
    case 'com.ping.image':
    case 'com.ping.gif': {
      // GIFs share the IMAGE numeric tag and render through the same
      // `ImageAttachmentCard` primitive — the `url` on `blob_ref` is
      // the Tenor/KLIPY CDN url (no fetch required) for GIFs and the
      // resolved local preview url for IMAGEs.
      const body = parsed.body as ImageBody;
      const aspectRatio = body.w > 0 && body.h > 0 ? body.w / body.h : 1;
      const state: ImageAttachmentState = isFailed
        ? 'failed'
        : 'uploaded';
      // Prefer a host-resolved, directly-viewable URL (decrypted object
      // URL) over the envelope's `blob_ref.url`, which is not viewable for
      // encrypted S3 media.
      const imageUri = ctx.image?.imageUri ?? body.blob_ref.url;
      const onTapOpen = ctx.image?.onTapOpen ?? noop;
      const onRetry = ctx.image?.onRetry;
      const progress = ctx.image?.progress;
      return {
        kind: 'image',
        state,
        aspectRatio,
        ...(body.blurhash !== undefined ? { blurhash: body.blurhash } : {}),
        ...(imageUri !== undefined ? { imageUri } : {}),
        ...(progress !== undefined ? { progress } : {}),
        // Stickers render chrome-free (no bubble background) — carried through
        // so thread tiles match the main chat.
        ...(body.sticker === true ? { isSticker: true as const } : {}),
        onTapOpen,
        ...(onRetry !== undefined ? { onRetry } : {}),
      };
    }
    case 'com.ping.video': {
      // Dedicated `video` variant → `<VideoAttachmentCard>`. Web expands
      // the poster into a full inline player (play/pause, PiP, fullscreen,
      // mute, download); native falls back to poster + `onTapOpen`.
      const body = parsed.body as VideoBody;
      const aspectRatio = body.w > 0 && body.h > 0 ? body.w / body.h : 1;
      const state: ImageAttachmentState = isFailed ? 'failed' : 'uploaded';
      // Host-resolved object URLs win; fall back to the (non-viewable for
      // encrypted media) envelope urls so display-only surfaces still show
      // a poster.
      const posterUri =
        ctx.video?.posterUri ?? body.poster_ref?.url ?? body.blob_ref.url;
      const videoUri = ctx.video?.videoUri ?? body.blob_ref.url;
      const onTapOpen = ctx.video?.onTapOpen ?? noop;
      const onRetry = ctx.video?.onRetry;
      const onDownload = ctx.video?.onDownload;
      const progress = ctx.video?.progress;
      const durationLabel =
        body.dur_ms > 0 ? formatMmSs(body.dur_ms) : undefined;
      return {
        kind: 'video',
        state,
        aspectRatio,
        ...(posterUri !== undefined ? { posterUri } : {}),
        ...(videoUri !== undefined ? { videoUri } : {}),
        ...(body.mime !== undefined ? { mimeType: body.mime } : {}),
        ...(durationLabel !== undefined ? { durationLabel } : {}),
        ...(body.caption !== undefined ? { caption: body.caption } : {}),
        ...(progress !== undefined ? { progress } : {}),
        onTapOpen,
        ...(onDownload !== undefined ? { onDownload } : {}),
        ...(onRetry !== undefined ? { onRetry } : {}),
      };
    }
    case 'com.ping.link_preview': {
      // Smart link-preview tile. The og: metadata is on the envelope; the
      // host injects `onOpen` (Linking.openURL) + an optional resolved
      // image object URL.
      const body = parsed.body as {
        url: string;
        title: string;
        desc?: string;
        image_ref?: { url?: string };
      };
      const imageUri = ctx.linkPreview?.imageUri ?? body.image_ref?.url;
      // Default to opening the URL via Linking so EVERY surface (main
      // chat, threads) gets a working tap without host wiring; hosts may
      // still override with their own `onOpen`.
      const onOpen =
        ctx.linkPreview?.onOpen ?? (() => void Linking.openURL(body.url).catch(() => undefined));
      return {
        kind: 'linkPreview',
        url: body.url,
        title:
          body.title !== undefined && body.title.length > 0
            ? body.title
            : body.url,
        ...(body.desc !== undefined && body.desc.length > 0
          ? { description: body.desc }
          : {}),
        ...(imageUri !== undefined ? { imageUri } : {}),
        ...(domainFromUrl(body.url) !== undefined
          ? { domain: domainFromUrl(body.url) }
          : {}),
        onOpen,
      };
    }
    case 'com.ping.file': {
      const body = parsed.body as FileBody;
      const state: DocAttachmentState = isFailed ? 'failed' : 'uploaded';
      const onTapPreview = ctx.doc?.onTapPreview ?? noop;
      const onRetry = ctx.doc?.onRetry;
      const progress = ctx.doc?.progress;
      const previewUri = ctx.doc?.previewUri;
      return {
        kind: 'doc',
        state,
        filename: body.name,
        mimeType: body.mime,
        byteSize: body.size,
        ...(body.page_count !== undefined ? { pageCount: body.page_count } : {}),
        ...(progress !== undefined ? { progress } : {}),
        ...(previewUri !== undefined ? { previewUri } : {}),
        onTapPreview,
        ...(onRetry !== undefined ? { onRetry } : {}),
      };
    }
    case 'com.ping.edit': {
      // Edits replace the parent — when the host wired the variant
      // map at all we know the row IS an edit (the upstream merge
      // collapsed parent → child). Render the `(edited)` footer with
      // the bubble variant by default.
      return {
        kind: 'edit',
        variant: 'bubble',
        editedAt: message.timestamp,
      };
    }
    case 'com.ping.call_invite': {
      const body = parsed.body as CallInviteBody;
      // Ongoing, joinable call tile (Figma 2701:6668). The host injects
      // `onJoinLeave` (and `joined`) — deriveVariant is pure, so it only carries
      // the ids needed to wire the action.
      return {
        kind: 'call',
        callState: 'ongoing',
        status: 'Call ongoing',
        durationLabel: body.media === 'video' ? 'Video call' : 'Voice call',
        startedAtMs: message.timestamp,
        timeLabel: hhMm(message.timestamp),
        actionable: true,
        callId: body.call_id,
        callGroupId: body.call_group_id,
        media: body.media,
      };
    }
    case 'com.ping.call_end': {
      const body = parsed.body as CallEndBody;
      // A connection failure (couldn't reach LiveKit / token mint / network)
      // is NOT a user decline — it renders in the missed VISUAL style but with
      // "Call failed" text, and stays actionable so the user can retry.
      const failed = body.reason === 'network_failure' || body.reason === 'failed';
      const missed =
        !failed &&
        (body.reason === 'declined' || body.reason === 'timeout' || body.reason === 'missed');
      // status = the bold line; subtitle = the detail line.
      let status: string;
      let subtitle: string;
      let callState: 'ended' | 'missed';
      if (failed) {
        status = 'Call failed';
        subtitle = "Couldn't connect";
        callState = 'missed';
      } else if (missed) {
        status = 'Call missed';
        subtitle = body.reason === 'declined' ? 'Call declined' : 'No answer';
        callState = 'missed';
      } else {
        status = 'Call ended';
        subtitle =
          body.duration_ms !== undefined && body.duration_ms > 0
            ? `Lasted ${formatMmSs(body.duration_ms)}`
            : 'Call ended';
        callState = 'ended';
      }
      // Ended → no CTA (green Phone). Missed/failed → PhoneMissed + "Tap to call back".
      return {
        kind: 'call',
        callState,
        status,
        durationLabel: subtitle,
        timeLabel: hhMm(message.timestamp),
        actionable: failed || missed,
        callId: body.call_id,
      };
    }
    case 'com.ping.group_name': {
      const body = parsed.body as GroupNameBody;
      const actor = body.actorDisplayName?.trim() || 'Someone';
      return {
        kind: 'event',
        type: 'groupRenamed',
        variant: 'compact',
        title: `${actor} renamed the group`,
        detail: `to "${body.name}"`,
      };
    }
    case 'com.ping.group_avatar': {
      const body = parsed.body as GroupAvatarBody;
      const actor = body.actorDisplayName?.trim() || 'Someone';
      return {
        kind: 'event',
        type: 'settingsChanged',
        variant: 'compact',
        title: `${actor} updated the group icon`,
      };
    }
    case 'com.ping.group_topic': {
      const body = parsed.body as GroupTopicBody;
      const actor = body.actorDisplayName?.trim() || 'Someone';
      return {
        kind: 'event',
        type: 'settingsChanged',
        variant: 'compact',
        title: `${actor} changed the topic`,
        detail: `to "${body.topic}"`,
      };
    }
    case 'com.ping.invite': {
      const body = parsed.body as InviteBody;
      const actor = body.actorDisplayName?.trim() || 'Someone';
      const invitee = body.inviteeDisplayName?.trim() || 'a new member';
      return {
        kind: 'event',
        type: 'userJoined',
        variant: 'compact',
        title: `${actor} invited ${invitee}`,
      };
    }
    case 'com.ping.kick': {
      const body = parsed.body as KickReasonBody;
      // Empty-string-safe: an emit site with an unhydrated profile sends
      // `''` (not undefined), which `??` would let through — rendering
      // "Alice removed " (blank). Trim + `||` catches both.
      const actor = body.actorDisplayName?.trim() || 'Someone';
      const target = body.targetDisplayName?.trim() || 'a member';
      // A voluntary leave rides the same envelope with `reason: 'left'` (target
      // is the leaver themselves) — render "X left", not "X removed X". On the
      // leaver's OWN local echo (`message.isSelf`), say "You left".
      const isLeave = body.reason === 'left';
      const leaveActor = message.isSelf ? 'You' : actor;
      return {
        kind: 'event',
        type: 'userLeft',
        variant: 'compact',
        title: isLeave ? `${leaveActor} left` : `${actor} removed ${target}`,
        ...(!isLeave && body.reason.length > 0 ? { detail: body.reason } : {}),
      };
    }
    case 'com.ping.pin': {
      const body = parsed.body as PinBody;
      const actor = body.actorDisplayName?.trim() || 'Someone';
      return {
        kind: 'event',
        type: 'messagePinned',
        variant: 'compact',
        title: `${actor} pinned a message`,
      };
    }
    case 'com.ping.unpin': {
      const body = parsed.body as UnpinBody;
      const actor = body.actorDisplayName?.trim() || 'Someone';
      return {
        kind: 'event',
        type: 'messageUnpinned',
        variant: 'compact',
        title: `${actor} unpinned a message`,
      };
    }

    case 'com.ping.game_create': {
      const body = parsed.body as GameCreateBody;
      return makeGameVariant('create', body.game_kind, body.session_id, body);
    }
    case 'com.ping.game_move': {
      const body = parsed.body as GameMoveBody;
      // The move body doesn't carry game_kind; the host resolves it
      // from its session state keyed on sessionId. Default to coinflip
      // as a harmless placeholder the host overrides.
      return makeGameVariant('move', 'coinflip', body.session_id, body);
    }
    case 'com.ping.game_outcome': {
      const body = parsed.body as GameOutcomeBody;
      return makeGameVariant('outcome', 'coinflip', body.session_id, body);
    }
    case 'com.ping.location': {
      // LocationBody = { lat, lon, accuracy_m, label?, live_duration_s? } —
      // the card prop uses `lng`, so map `lon` → `lng` here. A non-zero
      // `live_duration_s` marks a LIVE share; the expiry clock is the
      // message timestamp + duration (Figma 3629:6788 / 3629:7559).
      const body = parsed.body as {
        lat: number;
        lon: number;
        label?: string;
        live_duration_s?: number;
      };
      const isLive =
        typeof body.live_duration_s === 'number' && body.live_duration_s > 0;
      return {
        kind: 'location',
        lat: body.lat,
        lng: body.lon,
        ...(typeof body.label === 'string' ? { label: body.label } : {}),
        ...(isLive
          ? {
              live: true,
              liveUntilTs:
                ctx.message.timestamp + (body.live_duration_s as number) * 1000,
            }
          : {}),
      };
    }
    case 'com.ping.poll': {
      // Render the poll RESULT CARD from the envelope alone (options +
      // question), so surfaces without the host's live vote projection
      // (notably the thread panel) still show a real poll tile instead of
      // a "📊 Poll: …" text preview. The MAIN chat builds its own poll
      // variant WITH the live tally before ever reaching this fallback,
      // so this never overrides it. `onVote` is supplied by the host that
      // owns the vote projection; here it's a no-op (display-only).
      const body = parsed.body as {
        question: string;
        options: ReadonlyArray<string>;
        multi_select?: boolean;
        closes_at?: number;
      };
      const pollContent = {
        pollTarget: ctx.message.contentHash,
        question: body.question,
        options: body.options,
        multipleChoice: body.multi_select === true,
        ...(typeof body.closes_at === 'number' ? { closesAt: body.closes_at } : {}),
        creatorName: ctx.message.isSelf ? 'You' : '',
        createdAt: ctx.message.timestamp,
      };
      // Host-supplied live tally (main chat / thread panel). Mirrors the
      // 'poll' member of ChatMessageVariant exactly so voting works.
      if (ctx.poll !== undefined) {
        return {
          kind: 'poll',
          poll: pollContent,
          counts: ctx.poll.counts,
          ...(ctx.poll.myVote !== undefined ? { myVote: ctx.poll.myVote } : {}),
          ...(ctx.poll.myVotes !== undefined ? { myVotes: ctx.poll.myVotes } : {}),
          total: ctx.poll.total,
          ...(ctx.poll.votersByOption !== undefined
            ? { votersByOption: ctx.poll.votersByOption }
            : {}),
          onVote: ctx.poll.onVote,
          ...(ctx.poll.onViewVotes !== undefined
            ? { onViewVotes: ctx.poll.onViewVotes }
            : {}),
        } as ChatMessageVariant;
      }
      // Display-only fallback (no host projection available).
      return {
        kind: 'poll',
        poll: pollContent,
        counts: {},
        total: 0,
        onVote: () => undefined,
      } as ChatMessageVariant;
    }

    // MEETING_CREATE renders the in-chat meeting card. Live state (RSVP tally,
    // creator, applied updates/cancel) comes from `ctx.meeting`; when absent the
    // card is display-only from the create body. The update/rsvp/cancel sidecars
    // return `undefined` above and mutate the state the host folds.
    case 'com.ping.meeting': {
      const body = parsed.body as {
        meeting_id: string;
        title: string;
        starts_at: number;
        ends_at: number;
        location?: string;
        description?: string;
        meeting_link?: string;
        auto_call?: { media?: 'audio' | 'video' };
      };
      const m = ctx.meeting;
      const startsAt = m?.startsAt ?? body.starts_at;
      const endsAt = m?.endsAt ?? body.ends_at;
      const status =
        m?.status ??
        (Date.now() >= endsAt ? 'past' : Date.now() >= startsAt ? 'inProgress' : 'upcoming');
      const location = m?.location ?? body.location;
      const description = m?.description ?? body.description;
      const meetingLink = m?.meetingLink ?? body.meeting_link;
      // A `call`-type meeting exposes a Join action. Prefer the live-state flag;
      // fall back to the create body so the button shows before the fold lands.
      const autoCall =
        m?.autoCall ??
        (body.auto_call !== undefined
          ? { media: body.auto_call.media === 'video' ? ('video' as const) : ('audio' as const) }
          : undefined);
      const content = {
        meetingId: body.meeting_id,
        title: m?.title ?? body.title,
        startsAt,
        endsAt,
        ...(location !== undefined && location.length > 0 ? { location } : {}),
        ...(description !== undefined && description.length > 0 ? { description } : {}),
        ...(meetingLink !== undefined && meetingLink.length > 0 ? { meetingLink } : {}),
        ...(m?.reminderMinutes !== undefined ? { reminderMinutes: m.reminderMinutes } : {}),
        ...(autoCall !== undefined ? { autoCall } : {}),
        status,
        isCreator: m?.isCreator ?? ctx.message.isSelf,
      };
      return {
        kind: 'meeting',
        content,
        counts: m?.counts ?? { yes: 0, no: 0, maybe: 0 },
        ...(m?.myRsvp !== undefined ? { myRsvp: m.myRsvp } : {}),
        ...(m?.onRsvp !== undefined ? { onRsvp: m.onRsvp } : {}),
        ...(m?.onOpenLink !== undefined ? { onOpenLink: m.onOpenLink } : {}),
        ...(m?.onCopyLink !== undefined ? { onCopyLink: m.onCopyLink } : {}),
        ...(m?.onEdit !== undefined ? { onEdit: m.onEdit } : {}),
        ...(m?.onCancelMeeting !== undefined ? { onCancelMeeting: m.onCancelMeeting } : {}),
        ...(m?.onShowResponses !== undefined ? { onShowResponses: m.onShowResponses } : {}),
        ...(m?.onJoinCall !== undefined ? { onJoinCall: m.onJoinCall } : {}),
      } as ChatMessageVariant;
    }

    // Bubbles handled by `MessageBubble` directly — no variant needed:
    case 'com.ping.text':
    case 'com.ping.sticker':
    case 'com.ping.redact':
    case 'com.ping.forward':
    case 'com.ping.reaction':
    case 'com.ping.reaction_remove':
    case 'com.ping.thread_root':
    // Poll votes are a sidecar OR-set projected by `usePollVotes` — they
    // never render their own bubble; the vote tallies update the existing
    // poll bubble in place.
    case 'com.ping.poll_vote':
    // Notes + tasks are DeviceGroup-only personal state projected by
    // `useNotes` / `useTasks` — never a chat-room bubble.
    case 'com.ping.note_upsert':
    case 'com.ping.note_delete':
    case 'com.ping.task_upsert':
    case 'com.ping.task_delete':
    // Read pointers + activity reminders are DeviceGroup-only personal
    // state projected by the Activity feed / read-state layer — never a
    // chat-room bubble.
    case 'com.ping.read_pointer':
    case 'com.ping.reminder_set':
    case 'com.ping.reminder_clear':
    // Meeting update/rsvp/cancel are sidecars — they mutate the materialized
    // meeting state (resolved live by the MEETING_CREATE card) and render no
    // row of their own. Only MEETING_CREATE (`com.ping.meeting`) renders a card,
    // handled by the `com.ping.meeting` case below.
    case 'com.ping.meeting_update':
    case 'com.ping.meeting_rsvp':
    case 'com.ping.meeting_cancel':
    // Game lifecycle events with no standalone row — they mutate the
    // host's session state on the existing create/move rows:
    case 'com.ping.game_join':
    case 'com.ping.game_reveal':
    case 'com.ping.game_abort':
    // Call signaling beyond invite/end never renders a chat bubble — it
    // drives the live call UI via useCallSignaling. calls.md §5.3.10.
    case 'com.ping.call_accept':
    case 'com.ping.call_decline':
    case 'com.ping.call_ringing':
    case 'com.ping.call_hand_raise':
    case 'com.ping.call_reaction':
    case 'com.ping.call_roster':
    case 'com.ping.call_handoff':
      return undefined;
  }
}

/**
 * Builds a `GameVariant`. Cast through `ChatMessageVariant` because
 * the `game` union member is added by the integrator in
 * `ChatRoom.types.ts` (see the GameVariant TODO above) — once that
 * lands the cast becomes a no-op the type-checker proves sound.
 */
function makeGameVariant(
  phase: GameVariant['phase'],
  gameKind: GameKind,
  sessionId: string,
  payload: unknown,
): ChatMessageVariant {
  const variant: GameVariant = {
    kind: 'game',
    gameKind,
    sessionId,
    phase,
    payload,
  };
  return variant as unknown as ChatMessageVariant;
}

/**
 * Convenience: parse + derive in one call. Hosts that have the raw
 * `Message.body` string can call this directly.
 */
export function deriveVariantFromBody(
  body: string | Uint8Array | undefined | null,
  ctx: DeriveVariantCtx,
): ChatMessageVariant | undefined {
  return deriveMessageVariant(parseEnvelope(body), ctx);
}

/**
 * Pre-computes a variant map keyed by `Message.id` for an array of
 * messages — convenient for hosts that already maintain per-row
 * callback maps. `ctxFor(message)` returns the per-row context.
 */
export function deriveVariantsByMessageId(
  messages: ReadonlyArray<Message>,
  ctxFor: (message: Message) => DeriveVariantCtx,
): ReadonlyMap<string, ChatMessageVariant> {
  const out = new Map<string, ChatMessageVariant>();
  for (const m of messages) {
    const variant = deriveVariantFromBody(m.body, ctxFor(m));
    if (variant !== undefined) {
      out.set(m.id, variant);
    }
  }
  return out;
}

function pickVoiceState(
  message: Message,
  isFailed: boolean,
): VoiceMessageState {
  if (isFailed) return 'failed';
  if (message.deliveryState === 'pending') return 'sent';
  return message.isSelf ? 'sent' : 'received';
}

function formatMmSs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

// Clock label (HH:MM) for the call tile's send time.
function hhMm(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Bare host (e.g. "youtube.com") from a URL for the link-preview footer.
// Strips a leading "www." Returns undefined on an unparseable string so
// the card can omit the domain row.
function domainFromUrl(url: string): string | undefined {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

function noop(): void {
  // Default no-op when the host hasn't wired a callback — the variant
  // dispatch still renders the tile, but pressing it does nothing.
  // Preferable to throwing because some surfaces (read-only history)
  // intentionally skip wiring playback / open handlers.
}

// Re-export an event-message type union for hosts that build their own
// variant maps and want to match what this module produces.
export type DerivedEventVariantKind = Extract<EventMessageType,
  | 'callStarted'
  | 'callEnded'
  | 'groupRenamed'
  | 'settingsChanged'
  | 'userJoined'
  | 'userLeft'
  | 'messagePinned'
  | 'messageUnpinned'>;
